import clsx from 'clsx';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { default as MUIDialog, DialogProps as MUIDialogProps } from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';

import './Dialog.css';
import MaterialIcon from 'components/icon/MaterialIcon';
import React from 'react';

//
// --- Types ---

export interface DialogProps extends MUIDialogProps {
  /** content rendered in a `DialogActions` element if set */
  actionContent?: React.ReactNode | Array<React.ReactNode>;
  /** content rendered in a `DialogContent` element */
  content: React.ReactNode;
  /** apply styles to center content in the dialog */
  shouldCenterContent?: boolean;
  /** callback triggered when close button click, if displayed */
  onCloseButtonClick?: () => void;
  /** show close button in upper right hand corner; default is false */
  showCloseButton?: boolean;
  /** react testing library test attribute */
  'data-testid'?: string;
  /** content rendered in a `DialogTitle` element */
  title: string;
}

//
// --- Dialog Component ---

/**
 * Intended to be a base dialog component that handle basic styles for dialogs in this app
 */
const Dialog: React.FC<DialogProps> = ({
  actionContent,
  className,
  content,
  'data-testid': dataTestId,
  fullWidth = true,
  maxWidth = 'xs',
  onCloseButtonClick,
  shouldCenterContent = false,
  showCloseButton,
  title,
  ...remainingMUIDialogProps
}) => (
  <MUIDialog
    aria-describedby="dialog-content"
    aria-labelledby="dialog-title"
    className={clsx('dialog', className)}
    data-testid={dataTestId || 'dialog'}
    fullWidth={fullWidth}
    maxWidth={maxWidth}
    {...remainingMUIDialogProps}
  >
    <DialogTitle data-testid="dialog-title" id="dialog-title" disableTypography>
      {title}
      {showCloseButton ? (
        <IconButton aria-label="close" onClick={onCloseButtonClick}>
          <MaterialIcon name="close" />
        </IconButton>
      ) : null}
    </DialogTitle>
    <DialogContent data-testid="dialog-content" id="dialog-content">
      {shouldCenterContent ? <div className="dialog-content-center-container">{content}</div> : content}
    </DialogContent>
    {actionContent && <DialogActions data-testid="dialog-actions">{actionContent}</DialogActions>}
  </MUIDialog>
);

export default Dialog;
